import { useMemo, useCallback, useState, useRef, Dispatch, SetStateAction, RefObject } from 'react';
import { max, min } from 'd3';
import { metersToFeet, metersToKilometers, metersToMiles } from '@alltrails/data-formatting/distanceConverters';
import type { CategoricalChartFunc } from 'recharts/types/chart/types';
import type { MouseHandlerDataParam } from 'recharts';
import { ElevationData, ElevationPoint, LngLat } from '../types/Geo';
import getElevationDataMinMaxChartValues from '../utils/getElevationDataMinMaxChartValues';

type Args = {
  displayMetric?: boolean;
  elevationData: ElevationPoint[][];
  handleMouseOver: (coordinates: LngLat | null) => void;
  setCurrentGrade: Dispatch<SetStateAction<string | number>>;
  shouldRenderMinimalTicks?: boolean;
  tooltipRef: RefObject<HTMLDivElement | null>;
  activeSegmentIndex?: number;
};

export const averageGrade = (pointByPoint: ElevationPoint[], atIndex: number, displayMetric?: boolean) => {
  // Least-squares fit to points within 100m of selected point
  // Find at least one point ahead and behind, even if it's more than 100m
  const distanceSpan = 100.0 / (displayMetric ? 1000.0 : 1609.34);
  const centerDistance = pointByPoint?.[atIndex]?.[0] || 0;
  let xSum = 0;
  let ySum = 0;
  let xySum = 0;
  let xxSum = 0;
  let count = 0;
  // Look backwards from selected point for 100m, then look forwards from it
  let movingBackwards = true;
  let i = atIndex;
  for (;;) {
    if (i === pointByPoint.length) {
      break;
    }
    const distanceFromCenter = (pointByPoint?.[i]?.[0] || 0) - centerDistance;
    if (movingBackwards) {
      if (-distanceFromCenter > distanceSpan && i !== atIndex - 1) {
        movingBackwards = false;
        i = atIndex + 1;
      }
    } else if (distanceFromCenter > distanceSpan && i !== atIndex + 1) {
      break;
    }
    const x = (pointByPoint?.[i]?.[0] || 0) * (displayMetric ? 1000 : 5280);
    xSum += x;
    ySum += pointByPoint?.[i]?.[1] || 0;
    xxSum += x * x;
    xySum += x * (pointByPoint?.[i]?.[1] || 0);
    count += 1;
    if (movingBackwards) {
      i -= 1;
      if (i === -1) {
        movingBackwards = false;
        i = atIndex + 1;
      }
    } else {
      i += 1; // This is checked against bounds on next iteration
    }
  }
  if (count > 1 && count * xxSum - xSum * xSum !== 0.0) {
    const grade = (count * xySum - xSum * ySum) / (count * xxSum - xSum * xSum);
    return 100.0 * grade;
  }
  return 0;
};

const useElevationChart = ({
  displayMetric,
  elevationData,
  handleMouseOver,
  setCurrentGrade,
  shouldRenderMinimalTicks,
  tooltipRef,
  activeSegmentIndex
}: Args): {
  data: ElevationData;
  handleMouseMove: CategoricalChartFunc;
  showEmptyData: boolean;
  tooltipXPosition: number;
  xTicks: number[];
  yTicks: number[];
} => {
  const gradeCache = useRef<Record<string, number>>({});
  const [tooltipXPosition, setTooltipXPosition] = useState<number>(0);

  const { minActivePointIndex, maxActivePointIndex } = useMemo(() => {
    let min = 0;
    let max = elevationData.length - 1;

    if (activeSegmentIndex !== undefined && elevationData[activeSegmentIndex]) {
      min = elevationData.slice(0, activeSegmentIndex).reduce((acc, segment) => acc + segment.length, 0);
      max = min + elevationData[activeSegmentIndex].length - 2;
    }

    return {
      minActivePointIndex: min,
      maxActivePointIndex: max
    };
  }, [elevationData, activeSegmentIndex]);

  const flattenedData = useMemo(
    () =>
      elevationData
        .flat()
        .filter(segment => Boolean(segment?.length))
        .map(point => {
          const distance = displayMetric ? metersToKilometers(point[0]) : metersToMiles(point[0]);
          const elevation = displayMetric ? point[1] : metersToFeet(point[1]);
          return [distance, elevation, ...point.slice(2)] as ElevationPoint;
        }),
    [displayMetric, elevationData]
  );

  const showEmptyData = useMemo(() => flattenedData.length === 0, [flattenedData]);

  const data = useMemo<ElevationData>(
    () =>
      flattenedData.length < 1
        ? [{ distance: 0, elevation: 0, coordinates: [0, 0], index: 0 }]
        : flattenedData.map((dataPoint, index) => {
            const distance = dataPoint?.[0];
            const elevation = dataPoint?.[1];
            const coordinates = dataPoint?.[2];
            const currentTime = dataPoint?.[3]?.currentTime;
            const elapsedTime = dataPoint?.[3]?.elapsedTime;

            return {
              distance,
              elevation,
              // If this point is outside of the active segment return the elevation as inactiveElevation
              inactiveElevation: index < minActivePointIndex || index > maxActivePointIndex ? elevation : undefined,
              coordinates,
              index,
              currentTime,
              elapsedTime
            };
          }),
    [flattenedData, minActivePointIndex, maxActivePointIndex]
  );

  const xTicks = useMemo(() => {
    const maxX = max(flattenedData, d => d?.[0]) || 0;
    if (maxX === 0 || showEmptyData) {
      return [0, 100];
    }
    const xTickMultipliers = shouldRenderMinimalTicks ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1];
    return xTickMultipliers.map(mult => maxX * mult);
  }, [flattenedData, shouldRenderMinimalTicks, showEmptyData]);

  const yTicks = useMemo(() => {
    const minY = min(flattenedData, d => d?.[1]) || 0;
    const maxY = max(flattenedData, d => d?.[1]) || 0;
    return getElevationDataMinMaxChartValues(minY, maxY, displayMetric);
  }, [flattenedData, displayMetric]);

  const handleMouseMove = useCallback(
    (nextState?: MouseHandlerDataParam) => {
      const item = ['number', 'string'].includes(typeof nextState?.activeIndex) ? data[Number(nextState?.activeIndex)] : null;
      const coordinates = item?.coordinates;
      const cursorX = nextState?.activeCoordinate?.x || 0;
      const offset = 16;
      const tooltipWidth = tooltipRef.current?.clientWidth ?? 100;
      // position the tooltip to the left of the cursor when there is enough space to do so:
      const tooltipX = cursorX > tooltipWidth + offset ? cursorX - tooltipWidth - offset : cursorX + offset;
      setTooltipXPosition(tooltipX);

      if (!coordinates) {
        setCurrentGrade('');
        handleMouseOver?.(null);
      } else if (nextState?.activeTooltipIndex !== undefined && nextState.activeTooltipIndex !== null) {
        const index = Number(nextState?.activeTooltipIndex);
        const cacheKey = JSON.stringify(index);
        if (index && flattenedData.length > 3 && index > -1 && index < flattenedData.length - 1) {
          const grade = gradeCache?.current?.[cacheKey] || averageGrade(flattenedData, index, displayMetric);
          gradeCache.current[cacheKey] = grade;
          setCurrentGrade(grade ?? null);
        } else {
          setCurrentGrade('');
        }

        handleMouseOver?.([coordinates?.[1], coordinates?.[0]]);
      }
    },
    [data, tooltipRef, setCurrentGrade, handleMouseOver, flattenedData, displayMetric]
  );

  return { data, handleMouseMove, showEmptyData, tooltipXPosition, xTicks, yTicks };
};

export default useElevationChart;
